// Budget Health Thresholds Storage
// Manages configurable thresholds that drive the project health badge (green/amber/red)
// shown on the Project List, Dashboard, Portfolio, and Overview tab.
//
// Health is a multi-dimensional rollup across 6 dimensions:
//   1. Cost      — total cost consumed vs total cost budget
//   2. Schedule  — completion % vs timeline elapsed %
//   3. Hours     — hours consumed % vs completion % (gap signals over-resourcing)
//   4. WIP       — WIP amount and under-billing gap
//   5. Milestones — any overdue milestones
//   6. Margin    — actual gross margin % vs configured thresholds (Task 22)
//
// Health = worst single signal across all active dimensions.
// A project is only green if ALL evaluated dimensions are green.
//
// Phase 1: localStorage-backed (no backend required)
// Phase 2: replace read/write calls with API calls — interface stays identical

export interface BudgetHealthThresholds {
  // ── Dimension 1: Cost ──────────────────────────────────────────────────
  /** % of total cost budget consumed at which health turns amber. Default: 80 */
  costAmberThreshold: number;
  /** % of total cost budget consumed at which health turns red. Default: 110 */
  costRedThreshold: number;

  // ── Dimension 2: Schedule ──────────────────────────────────────────────
  /** % gap (timeElapsed - completion) at which health turns amber. Default: 10 */
  scheduleAmberGap: number;
  /** % gap (timeElapsed - completion) at which health turns red. Default: 20 */
  scheduleRedGap: number;

  // ── Dimension 3: Hours ─────────────────────────────────────────────────
  /** % gap (hoursConsumed% - completionPercent) at which health turns amber. Default: 15 */
  hoursAmberGap: number;
  /** % gap (hoursConsumed% - completionPercent) at which health turns red. Default: 30 */
  hoursRedGap: number;

  // ── Dimension 4: WIP / Billing ─────────────────────────────────────────
  /** WIP amount (£) at which health turns amber. Default: 30000 */
  wipAmberThreshold: number;
  /** WIP amount (£) at which health turns red. Default: 50000 */
  wipRedThreshold: number;
  /** Under-billing % gap at which health turns amber. Default: 5 */
  billingAmberGap: number;
  /** Under-billing % gap at which health turns red. Default: 10 */
  billingRedGap: number;

  // ── Dimension 5: Milestones ────────────────────────────────────────────
  /** Any overdue milestone triggers red. Default: true */
  milestoneRedOnAnyOverdue: boolean;

  // ── Dimension 6: Gross Margin (Task 22) ───────────────────────────────
  /** Actual GM% below this value turns amber. Default: 15 */
  marginAmberThreshold: number;
  /** Actual GM% below this value turns red. Default: 10 */
  marginRedThreshold: number;
  /**
   * When false, the margin dimension is excluded from health rollup.
   * Finance can disable this until nominal code mapping is ready.
   * Default: true
   */
  marginEnabled: boolean;

  /** Last updated ISO timestamp (stamped automatically on save) */
  lastUpdated?: string;
}

const STORAGE_KEY = 'budget-health-thresholds';

export const DEFAULT_BUDGET_HEALTH_THRESHOLDS: BudgetHealthThresholds = {
  // Cost
  costAmberThreshold: 80,
  costRedThreshold: 110,
  // Schedule
  scheduleAmberGap: 10,
  scheduleRedGap: 20,
  // Hours
  hoursAmberGap: 15,
  hoursRedGap: 30,
  // WIP / Billing
  wipAmberThreshold: 30000,
  wipRedThreshold: 50000,
  billingAmberGap: 5,
  billingRedGap: 10,
  // Milestones
  milestoneRedOnAnyOverdue: true,
  // Margin (Task 22)
  marginAmberThreshold: 15,
  marginRedThreshold: 10,
  marginEnabled: true,
};

/**
 * Load budget health thresholds from localStorage.
 * Returns defaults if nothing has been saved yet.
 *
 * Migration: legacy keys `amberThreshold` and `redThreshold` are transparently
 * promoted to `costAmberThreshold` and `costRedThreshold` so existing saved
 * values are preserved.
 */
export function getBudgetHealthThresholds(): BudgetHealthThresholds {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown>;

      // Migrate legacy field names
      if (typeof parsed.amberThreshold === 'number' && parsed.costAmberThreshold === undefined) {
        parsed.costAmberThreshold = parsed.amberThreshold;
      }
      if (typeof parsed.redThreshold === 'number' && parsed.costRedThreshold === undefined) {
        parsed.costRedThreshold = parsed.redThreshold;
      }

      return { ...DEFAULT_BUDGET_HEALTH_THRESHOLDS, ...parsed };
    }
  } catch (error) {
    console.error('Error loading budget health thresholds:', error);
  }
  return { ...DEFAULT_BUDGET_HEALTH_THRESHOLDS };
}

/**
 * Save budget health thresholds to localStorage.
 */
export function saveBudgetHealthThresholds(thresholds: BudgetHealthThresholds): void {
  try {
    const toSave: BudgetHealthThresholds = {
      ...thresholds,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving budget health thresholds:', error);
    throw error;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Health derivation params
// ─────────────────────────────────────────────────────────────────────────────

export interface DeriveProjectHealthParams {
  // Dimension 1 — Cost
  /** Total actual cost to date (labour + expenses + all other cost categories available) */
  actualCost: number;
  /** Total budget cost (labour + expenses + all other cost categories available) */
  budgetCost: number;

  // Dimension 2 — Schedule
  /** Project start date ISO string. If null/undefined, schedule dimension is skipped. */
  startDate?: string | null;
  /** Project end date ISO string. If null/undefined, schedule dimension is skipped. */
  endDate?: string | null;
  /** Effective completion % (override wins over system value). Range: 0–100. */
  completionPercent: number;

  // Dimension 3 — Hours
  /** Actual hours spent to date */
  actualHours: number;
  /** Total budgeted hours. If 0, hours dimension is skipped. */
  budgetHours: number;

  // Dimension 4 — WIP / Billing
  /** Current WIP amount (£) */
  wipAmount: number;
  /** Actual revenue invoiced to date */
  actualRevenue: number;
  /** Total revenue budget. If 0, billing gap dimension is skipped. */
  budgetRevenue: number;

  // Dimension 5 — Milestones
  /** Number of overdue milestones */
  overdueMilestoneCount: number;

  // Dimension 6 — Gross Margin (Task 22)
  /**
   * Actual gross margin % (0–100 scale, can be negative).
   * If undefined or null, margin dimension is skipped regardless of marginEnabled.
   */
  actualGrossMarginPct?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure health derivation function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pure function: derive a project health status from multi-dimensional signals
 * and the configured thresholds.
 *
 * Returns the WORST signal across all evaluated dimensions:
 *   green  — all dimensions healthy
 *   amber  — at least one dimension amber, none red
 *   red    — at least one dimension red
 *
 * Dimensions are skipped gracefully when data is unavailable (e.g. no end date,
 * zero budget hours, zero revenue budget).
 */
export function deriveProjectHealth(
  params: DeriveProjectHealthParams,
  thresholds: BudgetHealthThresholds
): 'green' | 'amber' | 'red' {
  const signals: ('green' | 'amber' | 'red')[] = [];

  // ── Dimension 1: Cost ────────────────────────────────────────────────────
  if (params.budgetCost > 0) {
    const costPercent = (params.actualCost / params.budgetCost) * 100;
    if (costPercent >= thresholds.costRedThreshold) {
      signals.push('red');
    } else if (costPercent >= thresholds.costAmberThreshold) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 2: Schedule ────────────────────────────────────────────────
  if (params.startDate && params.endDate) {
    const start = new Date(params.startDate).getTime();
    const end = new Date(params.endDate).getTime();
    const now = Date.now();
    const totalDuration = end - start;

    if (totalDuration > 0) {
      const timeElapsedPercent = Math.min(((now - start) / totalDuration) * 100, 100);
      const scheduleGap = timeElapsedPercent - params.completionPercent;

      if (scheduleGap >= thresholds.scheduleRedGap) {
        signals.push('red');
      } else if (scheduleGap >= thresholds.scheduleAmberGap) {
        signals.push('amber');
      } else {
        signals.push('green');
      }
    }
  }

  // ── Dimension 3: Hours ───────────────────────────────────────────────────
  if (params.budgetHours > 0) {
    const hoursConsumedPercent = (params.actualHours / params.budgetHours) * 100;
    const hoursGap = hoursConsumedPercent - params.completionPercent;

    if (hoursGap >= thresholds.hoursRedGap) {
      signals.push('red');
    } else if (hoursGap >= thresholds.hoursAmberGap) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 4a: WIP Amount ─────────────────────────────────────────────
  if (params.wipAmount >= thresholds.wipRedThreshold) {
    signals.push('red');
  } else if (params.wipAmount >= thresholds.wipAmberThreshold) {
    signals.push('amber');
  } else {
    signals.push('green');
  }

  // ── Dimension 4b: Billing Gap ────────────────────────────────────────────
  if (params.budgetRevenue > 0) {
    const billedPercent = (params.actualRevenue / params.budgetRevenue) * 100;
    const billingGap = params.completionPercent - billedPercent; // positive = under-billing

    if (billingGap >= thresholds.billingRedGap) {
      signals.push('red');
    } else if (billingGap >= thresholds.billingAmberGap) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 5: Milestones ──────────────────────────────────────────────
  if (thresholds.milestoneRedOnAnyOverdue && params.overdueMilestoneCount > 0) {
    signals.push('red');
  } else {
    signals.push('green');
  }

  // ── Dimension 6: Gross Margin (Task 22) ─────────────────────────────────
  // Only evaluated when marginEnabled is true AND actualGrossMarginPct is available.
  if (thresholds.marginEnabled && params.actualGrossMarginPct != null) {
    const gm = params.actualGrossMarginPct;
    if (gm < thresholds.marginRedThreshold) {
      signals.push('red');
    } else if (gm < thresholds.marginAmberThreshold) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Rollup: return worst signal ──────────────────────────────────────────
  if (signals.includes('red')) return 'red';
  if (signals.includes('amber')) return 'amber';
  return 'green';
}
